import React, { useMemo } from "react";
import {
  formatTournamentAmount,
  formatTournamentDate,
  payChipClass,
  resultBadgeClass,
  tournamentStatusBadgeClass,
} from "../../utils/myTournamentsDirectoryHelpers";

function resolveSummary(summary, directory) {
  if (!summary) return null;
  const primary =
    directory?.find((d) => d.tournament_id === summary.tournament_id) ||
    directory?.find((d) => d.id === summary.id) ||
    directory?.[0];
  if (!primary) return summary;

  return {
    ...summary,
    sport_type: summary.sport_type || primary.sport_type,
    tournament_level: summary.tournament_level || primary.tournament_level,
    location: summary.location || primary.location,
    participation_status: summary.participation_status || primary.participation_status,
    participation_fee: summary.participation_fee ?? primary.participation_fee,
    payment_id: summary.payment_id ?? primary.payment_id,
    payment_amount: summary.payment_amount ?? primary.payment_amount,
    payment_status:
      summary.payment_status && summary.payment_status !== "No payment"
        ? summary.payment_status
        : primary.payment_status || summary.payment_status,
    payment_verified: summary.payment_verified ?? primary.payment_verified,
    receipt_no: summary.receipt_no || primary.receipt_no,
    transaction_id: summary.transaction_id || primary.transaction_id,
    payment_date: summary.payment_date || primary.payment_date,
    payment_mode: summary.payment_mode || primary.payment_mode,
  };
}

function MetaItem({ label, value, emphasis }) {
  return (
    <div className={`mm-summary-meta${emphasis ? " mm-summary-meta--emphasis" : ""}`}>
      <span className="mm-summary-meta__label">{label}</span>
      <span className="mm-summary-meta__value">{value}</span>
    </div>
  );
}

export default function MyTournamentsSummarySection({ summary, directory, loading }) {
  const resolved = useMemo(
    () => resolveSummary(summary, directory),
    [summary, directory]
  );

  if (loading) {
    return (
      <section className="user-builder-panel mm-summary-panel" aria-label="Tournament summary">
        <div className="mm-summary-skeleton" aria-hidden />
      </section>
    );
  }

  if (!resolved) {
    return (
      <section className="user-builder-panel mm-summary-panel" aria-label="Tournament summary">
        <p className="mm-empty">No tournament participations to summarise yet.</p>
      </section>
    );
  }

  const statusKey = String(resolved.status || "muted").toLowerCase();
  const amountPaid = formatTournamentAmount(
    resolved.payment_amount ?? resolved.participation_fee
  );
  const participationFee = formatTournamentAmount(resolved.participation_fee);
  const hasPayment =
    resolved.payment_id != null ||
    resolved.payment_amount != null ||
    resolved.payment_status === "Paid" ||
    resolved.payment_status === "Pending";

  return (
    <section className="user-builder-panel mm-summary-panel" aria-label="Tournament summary">
      <div className="user-builder-panel__head">
        <div>
          <h3 className="user-builder-panel__title">Tournament summary</h3>
          <p className="user-builder-panel__hint">
            Currently active or latest tournament participation.
          </p>
        </div>
      </div>

      <article className={`mm-summary-card mm-summary-card--${statusKey}`}>
        <div className="mm-summary-card__top">
          <div className="mm-summary-card__identity">
            <span className="mm-summary-card__plan">
              {resolved.tournament_name || "Tournament"}
            </span>
            <span className="mm-summary-card__id">{resolved.tournament_id || "—"}</span>
          </div>
          <div className="mm-summary-card__badges">
            <span className={tournamentStatusBadgeClass(resolved.status)}>
              {resolved.status || "—"}
            </span>
            <span className={resultBadgeClass(resolved.result_status)}>
              {resolved.result_status || "Pending"}
            </span>
          </div>
        </div>

        <div className="mm-summary-card__stats">
          <MetaItem
            label="Tournament date"
            value={formatTournamentDate(resolved.tournament_date)}
          />
          <MetaItem
            label="Amount paid"
            value={hasPayment ? amountPaid : "—"}
            emphasis
          />
          <MetaItem label="Participation fee" value={participationFee} />
          <div className="mm-summary-meta mm-summary-meta--status">
            <span className="mm-summary-meta__label">Payment</span>
            <span className={payChipClass(resolved.payment_status)}>
              {resolved.payment_status || "No payment"}
            </span>
          </div>
        </div>

        {hasPayment ? (
          <footer className="mm-summary-card__footer">
            <span>
              <strong>Date</strong> {formatTournamentDate(resolved.payment_date)}
            </span>
            <span className="mm-summary-card__dot" aria-hidden>
              ·
            </span>
            <span>
              <strong>Mode</strong> {resolved.payment_mode || "—"}
            </span>
            <span className="mm-summary-card__dot" aria-hidden>
              ·
            </span>
            <span>
              <strong>Txn</strong>{" "}
              <code>{resolved.transaction_id || "—"}</code>
            </span>
            {resolved.receipt_no ? (
              <>
                <span className="mm-summary-card__dot" aria-hidden>
                  ·
                </span>
                <span>
                  <strong>Receipt</strong> <code>{resolved.receipt_no}</code>
                </span>
              </>
            ) : null}
          </footer>
        ) : null}

        <footer className="mm-summary-card__footer mm-summary-card__footer--meta">
          <span>
            <strong>Sport</strong> {resolved.sport_type || "—"}
          </span>
          <span className="mm-summary-card__dot" aria-hidden>
            ·
          </span>
          <span>
            <strong>Level</strong> {resolved.tournament_level || "—"}
          </span>
          <span className="mm-summary-card__dot" aria-hidden>
            ·
          </span>
          <span>
            <strong>Venue</strong> {resolved.location || "—"}
          </span>
          <span className="mm-summary-card__dot" aria-hidden>
            ·
          </span>
          <span>
            <strong>Registration</strong> {resolved.participation_status || "—"}
          </span>
        </footer>
      </article>
    </section>
  );
}
